import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, Lock, ChevronRight,
  UserPlus, UserMinus, ArrowLeftRight, DollarSign,
  Key, Zap, Database, GitBranch, LayoutDashboard, Activity, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingLayout } from '../components/marketing/MarketingLayout';

const workflows = [
  { icon: UserPlus, title: 'New Hire Onboarding', desc: 'Provisions IdP accounts, assigns device profiles, grants app access based on role' },
  { icon: UserMinus, title: 'Termination Offboarding', desc: 'Revokes IdP sessions, triggers MDM wipe, transfers data ownership' },
  { icon: ArrowLeftRight, title: 'Department Transfers', desc: 'Updates group memberships, reassigns assets, logs access changes' },
  { icon: Key, title: 'Access Requests', desc: 'Creates time-bound grants, requires approval chain, logs evidence' },
  { icon: Lock, title: 'Password & MFA Resets', desc: 'Verifies identity via HRIS, resets credentials, notifies user' },
  { icon: DollarSign, title: 'License Reclamation', desc: 'Identifies inactive seats via usage data, revokes unused licenses' }
];

const capabilities = [
  { icon: LayoutDashboard, title: 'Control Tower', desc: 'Unified operations dashboard for all IT lifecycle cases' },
  { icon: Zap, title: 'Action Bundles', desc: 'Pre-built automation sequences with policy gates' },
  { icon: Shield, title: 'Policy Studio', desc: 'Simulate and enforce governance rules before deployment' },
  { icon: Database, title: 'Asset Graph', desc: 'Complete inventory of users, devices, apps, and relationships' },
  { icon: GitBranch, title: 'Connectors', desc: '50+ integrations with IdP, HRIS, MDM, and SaaS systems' },
  { icon: Activity, title: 'Audit Trail', desc: 'Full evidence logging with 7-year retention' }
];

const architectureDiagram = () => (
  <svg viewBox="0 0 600 280" className="w-full h-auto">
    <defs>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#808080" />
      </marker>
    </defs>

    {/* Request Sources */}
    <g transform="translate(20, 20)">
      <rect x="0" y="0" width="100" height="80" rx="4" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" />
      <text x="50" y="25" textAnchor="middle" className="text-xs font-medium fill-slate-700">Request Sources</text>
      <text x="50" y="45" textAnchor="middle" className="text-[10px] fill-slate-500">Slack • Teams</text>
      <text x="50" y="58" textAnchor="middle" className="text-[10px] fill-slate-500">Email • Portal</text>
    </g>

    {/* Arrow to Control Tower */}
    <line x1="130" y1="60" x2="180" y2="60" stroke="#808080" strokeWidth="1" markerEnd="url(#arrowhead)" />

    {/* Control Tower */}
    <g transform="translate(190, 10)">
      <rect x="0" y="0" width="220" height="100" rx="4" fill="#4a70ff" fillOpacity="0.1" stroke="#4a70ff" strokeWidth="1" />
      <text x="110" y="25" textAnchor="middle" className="text-xs font-semibold fill-slate-800">Control Tower</text>
      <rect x="10" y="35" width="95" height="25" rx="2" fill="white" stroke="#e2e8f0" />
      <text x="57" y="52" textAnchor="middle" className="text-[9px] fill-slate-600">Case Management</text>
      <rect x="115" y="35" width="95" height="25" rx="2" fill="white" stroke="#e2e8f0" />
      <text x="162" y="52" textAnchor="middle" className="text-[9px] fill-slate-600">Action Bundles</text>
      <rect x="10" y="65" width="95" height="25" rx="2" fill="white" stroke="#e2e8f0" />
      <text x="57" y="82" textAnchor="middle" className="text-[9px] fill-slate-600">Policy Engine</text>
      <rect x="115" y="65" width="95" height="25" rx="2" fill="white" stroke="#e2e8f0" />
      <text x="162" y="82" textAnchor="middle" className="text-[9px] fill-slate-600">Approval Gates</text>
    </g>

    {/* Arrow to Systems */}
    <line x1="420" y1="60" x2="470" y2="60" stroke="#808080" strokeWidth="1" markerEnd="url(#arrowhead)" />

    {/* Target Systems */}
    <g transform="translate(480, 20)">
      <rect x="0" y="0" width="100" height="80" rx="4" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" />
      <text x="50" y="25" textAnchor="middle" className="text-xs font-medium fill-slate-700">Target Systems</text>
      <text x="50" y="45" textAnchor="middle" className="text-[10px] fill-slate-500">IdP • HRIS</text>
      <text x="50" y="58" textAnchor="middle" className="text-[10px] fill-slate-500">MDM • SaaS</text>
    </g>

    {/* Evidence & Audit */}
    <g transform="translate(190, 130)">
      <rect x="0" y="0" width="220" height="50" rx="4" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" />
      <text x="110" y="20" textAnchor="middle" className="text-xs font-medium fill-slate-700">Evidence & Audit</text>
      <text x="110" y="38" textAnchor="middle" className="text-[10px] fill-slate-500">Full action logging • Compliance reports • 7-year retention</text>
    </g>

    {/* Vertical arrow from Control Tower to Evidence */}
    <line x1="300" y1="110" x2="300" y2="125" stroke="#808080" strokeWidth="1" markerEnd="url(#arrowhead)" />

    {/* Domains */}
    <g transform="translate(20, 200)">
      <rect x="0" y="0" width="560" height="60" rx="4" fill="white" stroke="#e2e8f0" strokeWidth="1" />
      <text x="280" y="20" textAnchor="middle" className="text-xs font-medium fill-slate-700">Domain Coverage</text>
      <g transform="translate(20, 30)">
        <text x="0" y="15" className="text-[10px] fill-slate-600">Identity</text>
        <text x="90" y="15" className="text-[10px] fill-slate-600">Endpoints</text>
        <text x="180" y="15" className="text-[10px] fill-slate-600">SaaS</text>
        <text x="250" y="15" className="text-[10px] fill-slate-600">Security</text>
        <text x="330" y="15" className="text-[10px] fill-slate-600">Assets</text>
        <text x="410" y="15" className="text-[10px] fill-slate-600">Config</text>
        <text x="480" y="15" className="text-[10px] fill-slate-600">Cost</text>
      </g>
    </g>
  </svg>
);

export const Landing = () => {
  return (
    <MarketingLayout>
      <div data-testid="landing-page">
        {/* Hero Section */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
                  IT Lifecycle Management Platform
                </h1>
                <p className="text-base text-muted-foreground mb-6 leading-relaxed">
                  Automate identity provisioning, access management, and asset lifecycle operations.
                  Connect to IdP, MDM, HRIS, and SaaS systems. Enforce governance policies.
                  Maintain complete audit evidence.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button asChild size="default">
                    <Link to="/demo/request">
                      Request Demo
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                  <Button asChild size="default" variant="outline">
                    <Link to="/pilot">Start Pilot</Link>
                  </Button>
                  <Button asChild size="default" variant="ghost">
                    <Link to="/portal">Try the Demo</Link>
                  </Button>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="bg-secondary/50 border border-border rounded-lg p-4">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Platform Capabilities</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {[
                      'Identity provisioning',
                      'Access management',
                      'Asset lifecycle',
                      'Audit logging',
                      'Policy enforcement',
                      '50+ connectors'
                    ].map((cap) => (
                      <div key={cap} className="flex items-center gap-2 text-foreground">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        {cap}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-2">How It Works</h2>
            <p className="text-muted-foreground mb-8">
              Requests flow through Control Tower, where policies determine automation level and approval requirements.
            </p>
            <div className="border border-border rounded-lg p-6 bg-secondary/30">
              {architectureDiagram()}
            </div>
          </div>
        </section>

        {/* Capabilities Grid */}
        <section className="py-16 px-6 bg-secondary/50 border-y border-border">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-semibold text-foreground">Platform Capabilities</h2>
              <Link
                to="/product"
                className="inline-flex items-center gap-1 text-primary font-medium hover:underline text-sm"
              >
                View all
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {capabilities.map((cap, idx) => (
                <div key={idx} className="p-5 bg-white rounded-lg border border-border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <cap.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-1">{cap.title}</h3>
                      <p className="text-sm text-muted-foreground leading-snug">{cap.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Workflows Grid */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-semibold text-foreground">Core Workflows</h2>
              <Link
                to="/product/automation-builder"
                className="inline-flex items-center gap-1 text-primary font-medium hover:underline text-sm"
              >
                View templates
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {workflows.map((workflow, idx) => (
                <div key={idx} className="p-5 bg-secondary/30 rounded-lg border border-border">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white border border-border flex items-center justify-center shrink-0">
                      <workflow.icon className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-1">{workflow.title}</h3>
                      <p className="text-sm text-muted-foreground leading-snug">{workflow.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 bg-primary/5 border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Start Your Evaluation</h2>
            <p className="text-muted-foreground mb-8">
              Deploy in your environment within 2 weeks. Read-only connectors first,
              then enable automation with governance controls.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/pilot">View Pilot Plan</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default Landing;
